import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore, FilterList } from '@mui/icons-material';
import { RegionalOfficeSelect } from '~/components/RegionalOfficeSelect';
import type { KpiDateRangePreset } from '~/apis/analyticsApi';
import { DATE_RANGE_PRESET_OPTIONS, type DashboardFilterState } from '../types';

type DashboardFiltersProps = {
  filters: DashboardFilterState;
  onFiltersChange: (next: DashboardFilterState) => void;
  showRegionalFilter: boolean;
  showDateRangeFilter?: boolean;
};

export function DashboardFilters({
  filters,
  onFiltersChange,
  showRegionalFilter,
  showDateRangeFilter = true,
}: DashboardFiltersProps) {
  const setDateRange = (dateRangePreset: KpiDateRangePreset) => {
    onFiltersChange({ ...filters, dateRangePreset });
  };

  const setRegionalCode = (regionalCode: string) => {
    onFiltersChange({ ...filters, regionalCode });
  };

  if (!showRegionalFilter && !showDateRangeFilter) {
    return null;
  }

  return (
    <Accordion
      defaultExpanded
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: '12px !important',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterList fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Dashboard Filters
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        >
          {showRegionalFilter ? (
            <Box sx={{ minWidth: { xs: '100%', sm: 280 }, flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 0.5, mb: 0.75, display: 'block' }}
              >
                REGIONAL OFFICE
              </Typography>
              <RegionalOfficeSelect
                labelId="dashboard-regional-office-label"
                value={filters.regionalCode}
                onChange={setRegionalCode}
              />
            </Box>
          ) : null}
          {showDateRangeFilter ? (
            <Box sx={{ minWidth: { xs: '100%', sm: 220 }, flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 0.5, mb: 0.75, display: 'block' }}
              >
                DATE RANGE
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  labelId="dashboard-date-range-label"
                  label="Date range"
                  value={filters.dateRangePreset}
                  onChange={(e) => setDateRange(e.target.value as KpiDateRangePreset)}
                >
                  {DATE_RANGE_PRESET_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
